import React from "react";
import config from "../../config";
import Grid from "@material-ui/core/Grid";
import { translate } from "../../i18n/customI18nProvider";
import { Button, Chip } from "@mui/material";

import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, MouseSensor, TouchSensor } from "@dnd-kit/core";
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import ChromeReaderMode from '@mui/icons-material/ChromeReaderMode';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ConsumptionForm from "./ConsumptionForm";
import { Add, WatchOff } from "@mui/icons-material";
import { getBarConsumptions, reoderBarConsumptions } from "../../services/bar.service";
import { notifyError, notifySuccess } from "../../util";
import { useNotify } from "ra-core";
      
const styles = {
    consumptionItem: {
        container: {
            display: "flex",
            flex: 1,
            justifyContent: "space-between",
            border: "1px solid lightgray",
            borderRadius: "5px",
            padding: "10px",
            paddingTop: "20px",
            paddingBottom: "20px",
            userSelect: "none",
            WebkitUserSelect: "none",
            touchAction: "pan-y"
        },
        button: {
            textAlign: "center",
            alignContent: "center",
            background: "lightGray",
            borderRadius: "5px",
            height: "30px",
            width: "30px",
        }
    }
}

const ConsumptionList = ({ deposits, max_consumption_price, currencyCode, bar_id }) => {
    const [consumptionList, setConsumptionList] = React.useState([]);
    const [consumptionForm, setConsumptionForm] = React.useState({show: false, consumption: null});

    const notify = useNotify();
    
    
    const mouseSensor = useSensor(MouseSensor, {
      // Require the mouse to move by 10 pixels before activating
      activationConstraint: {
        distance: 10,
      },
    });
    const touchSensor = useSensor(TouchSensor, {
      // Press delay of 250ms, with tolerance of 5px of movement
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    });
    
    const sensors = useSensors(
      mouseSensor,
      touchSensor
    );
    
    const handleFormClose = () => {
        setConsumptionForm({ show: false, consumption: null });
        fetchConsumptions();
    }
    
    const handleDragStart = (event) => {
        if (window.navigator && window.navigator.vibrate) {
          window.navigator.vibrate(25);
        }
    };
    
    const handleDragEnd = async (event) => {
        const { active, over } = event;
        
        if (over && active.id !== over.id) {
            const newConsumptionList = arrayMove(
                consumptionList,
                consumptionList.findIndex((i) => i._id === active.id),
                consumptionList.findIndex((i) => i._id === over.id)
            );
            for(let i=0; i<newConsumptionList.length; i++) {
                newConsumptionList[i].index = i;
            }
            setConsumptionList(newConsumptionList);
            const response = await reoderBarConsumptions(bar_id, newConsumptionList);
            if (response.error) {
                notifyError(response.error);
            } else {
                notifySuccess("custom.order_saved");
            }
        }
    };

    React.useEffect(() => {
        fetchConsumptions();
    }, []);
    
    const fetchConsumptions = async () => {
        try {
            const consumptions = await getBarConsumptions(bar_id);
            if (consumptions && consumptions.length >= 0) {
                consumptions.sort((a, b) => a.index - b.index);
                setConsumptionList(consumptions);
            }
        } catch (error) {
            console.error("Error fetching consumptions:", error);
        }
    };
    
    const findDeposit = (depositId) => {
        const deposit = deposits.find((d) => d._id === depositId);
        return deposit ? deposit : null;
    };
    
    const findConsumptionColor = (colorId, displayed = true) => {
        const colorObj = config.consumption_colors.find((color) => color.id === colorId);
        console.log("Finding color for consumption with colorId:", colorId, "Found colorObj:", colorObj);
        if(!displayed) {
            // Add opacity to the color if the consumption is not currently displayed to the user (because of date or hidden)
            return colorObj ? colorObj.colorCode + "30" : config.baseColorLight + "30";
        }
        return colorObj ? colorObj.colorCode : config.baseColorLight;
    };
    
    const formatPrice = (price, depositId) => {
        let depositText = "";
        const deposit = findDeposit(depositId);
        if (deposit && deposit.price) {
            depositText = ` + ${formatPrice(deposit.price)} ${translate("custom.deposit")}`;
        }
        return `${currencyCode} ${price.toFixed(2)} ${depositText}`;
    }
    
    const formatAge = (min_age) => {
        if (min_age == 0 || min_age == null) {
            return null;
        } else if (min_age == 16) {
            return <Chip style={{marginLeft: "10px", fontSize: "11px"}} label={translate("custom.age_reverse_map.LESS_18")} />;
        } else if (min_age == 18) {
            return <Chip style={{marginLeft: "10px", fontSize: "11px"}} label={translate("custom.age_reverse_map.MORE_18")} />;
        }
    }
    
    const SortableConsumptionItem = ({ consumption, formatPrice, findConsumptionColor, baseColor }) => {
        const {
            attributes,
            listeners,
            setNodeRef,
            transform,
            transition,
            isDragging,
        } = useSortable({ id: consumption._id });

        function correctDate() {
            const now = new Date();
            const startDate = new Date(consumption.start_date);
            const endDate = new Date(consumption.end_date);
            console.log(consumption.name, startDate, endDate, now);
            if (startDate < now && endDate > now) {
                return true;
            }
            return false;
        }

        function displayedToUser() {
            if (consumption.hidden) {
                return false;
            }
            if (!correctDate()) {
                return false;
            }
            return true;
        }
    
        const style = {
            display: "flex",
            position: "relative",
            transform: CSS.Transform.toString(transform),
            transition,
            cursor: isDragging ? "grabbing" : "grab",
            opacity: isDragging ? 0.5 : 1,
            zIndex: isDragging ? 999 : 1,
            marginBottom: "10px",
        };
        const inputQuantityWidth = consumption.type == config.consumption_types.QUANTITY ? 30 : 90;
        return (
            <div style={style} ref={setNodeRef} {...attributes} {...listeners}>
                <div onClick={() => { setConsumptionForm({ show: true, consumption: consumption }) }} style={{ ...styles.consumptionItem.container, background: findConsumptionColor(consumption.color, displayedToUser()) }}>
                    <div>
                        <div style={{ display: "flex", alignItems: "center", marginBottom: "5px" }}>
                            {consumption.can_buy_only_with_voucher && (
                                <ChromeReaderMode style={{ fontSize: "20px", color: config.baseColor, marginRight: "5px" }} />
                            
                            )}
                            <h4 style={{ fontSize: "15px", color: config.baseColor, margin: "0px" }}>{consumption.name}</h4>
                        </div>
                        <div style={{ display: "flex", alignItems: "center"}}>
                            {consumption.hidden && (
                                <VisibilityOffIcon style={{ fontSize: "20px", color: config.baseColor, marginRight: "5px" }} />
                            
                            )}
                            {!correctDate() && (
                                <WatchOff style={{ fontSize: "20px", color: config.baseColor, marginRight: "5px" }} />
                            
                            )}
                            <p style={{ margin: "0px", fontSize: "11px" }}>{formatPrice(consumption.price, consumption.deposit)}</p>
                        </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px"}}>
                        <div>
                            {formatAge(consumption.min_age)}
                        </div>
                        <div 
                            style={{ display: "flex", background: "white", padding: "5px", borderRadius: "5px", height: "30px", marginTop: "auto", marginBottom: "auto" }}
                        >
                            {consumption.type == "quantity" && (<div style={styles.consumptionItem.button}>-</div>)}
                            <p style={{ margin: "0px", width: (inputQuantityWidth) + "px", textAlign: "center", alignSelf: "center" }}>{consumption.type == config.consumption_types.INPUT ? "0.00" : "0"}</p>
                            {consumption.type == "quantity" && (<div style={styles.consumptionItem.button}>+</div>)}
                        </div>
                    </div>
                </div>
            </div>
        )
    };
    
    return (
        <div>
            <div style={{ width: "100%" }}>
                <h3 style={{ width: "100%", borderBottom: "2px solid" + config.baseColor, color: config.baseColor }}>
                    {translate("custom.consumptions")}
                </h3>
            </div>
            <div style={{ maxWidth: "800px", margin: "auto" }}>
                <DndContext 
                    sensors={sensors} 
                    collisionDetection={closestCenter} 
                    onDragEnd={handleDragEnd}
                    onDragStart={handleDragStart}
                >
                    <SortableContext 
                        items={consumptionList.map(c => c._id)} 
                        strategy={verticalListSortingStrategy}
                    >
                        {consumptionList.map((consumption) => (
                            <Grid item xs={12} key={consumption._id}>
                                <SortableConsumptionItem 
                                    consumption={consumption}
                                    formatPrice={formatPrice}
                                    findConsumptionColor={findConsumptionColor}
                                />
                            </Grid>
                        ))}
                    </SortableContext>
                </DndContext>
                <div style={{ ...styles.consumptionItem.container, justifyContent: "center", alignItems: "center", background: "white", cursor: "pointer" }} onClick={() => { setConsumptionForm({ show: true, consumption: null }) }}>
                    <Button startIcon={<Add />}>
                        {translate('custom.add')}
                    </Button>
                </div>  
            </div>
            <ConsumptionForm max_consumption_price={max_consumption_price} barId={bar_id} barConsumptions={consumptionList} currencyCode={currencyCode} show={consumptionForm.show} consumption={consumptionForm.consumption} deposits={ deposits } onClose={ handleFormClose } />
        </div>
    );
};

export default ConsumptionList;
